import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import api from '../utils/api';
import { toast } from 'sonner';
import { UserPlus, Users } from 'lucide-react';

const platforms = ['Facebook', 'Instagram', 'LinkedIn', 'Twitter/X', 'WhatsApp', 'TikTok', 'Other'];
const statuses = ['New Lead', 'Contacted', 'Appointment Set', 'Presented', 'Objection Handling', 'Recruit/Enrolled', 'Not a Fit'];
const markets = ['Hot', 'Warm', 'Cultivated', 'Cold', 'Business Owner'];

export const QuickAddProspectModal = ({ open, onClose, onProspectAdded }) => {
  const [activeTab, setActiveTab] = useState('single');
  
  // Single add state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    platform: 'Facebook',
    message: '',
    status: 'New Lead',
    market: 'Warm',
    add_to_lists: [],
    date: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);

  // Bulk add state
  const [bulkText, setBulkText] = useState('');
  const [bulkMarket, setBulkMarket] = useState('Warm');
  const [bulkStatus, setBulkStatus] = useState('New Lead');
  const [bulkPlatform, setBulkPlatform] = useState('Other');
  const [bulkLoading, setBulkLoading] = useState(false);

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/prospects', formData);
      toast.success('Prospect added successfully!');
      if (onProspectAdded) onProspectAdded(response.data);
      setFormData({
        name: '',
        phone: '',
        platform: 'Facebook',
        message: '',
        status: 'New Lead',
        market: 'Warm',
        add_to_lists: [],
        date: new Date().toISOString().split('T')[0]
      });
      onClose();
      window.dispatchEvent(new CustomEvent('prospect-added'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add prospect');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSubmit = async () => {
    const lines = bulkText.split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      toast.error('Please enter at least one prospect');
      return;
    }

    setBulkLoading(true);
    try {
      const response = await api.post('/prospects/bulk', {
        raw_lines: lines,
        market: bulkMarket,
        status: bulkStatus,
        platform: bulkPlatform
      });
      
      toast.success(`Successfully added ${response.data.created_count} prospects!`);
      setBulkText('');
      onClose();
      window.dispatchEvent(new CustomEvent('prospect-added'));
    } catch (error) {
      toast.error('Failed to add prospects');
    } finally {
      setBulkLoading(false);
    }
  };

  const toggleList = (list) => {
    setFormData(prev => ({
      ...prev,
      add_to_lists: prev.add_to_lists.includes(list)
        ? prev.add_to_lists.filter(l => l !== list)
        : [...prev.add_to_lists, list]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="quick-add-prospect-modal" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#E31837]">Add Prospects</DialogTitle>
          <DialogDescription>Add a single prospect or bulk import from social media</DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="single" className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Single Add
            </TabsTrigger>
            <TabsTrigger value="bulk" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Bulk Add
            </TabsTrigger>
          </TabsList>

          {/* Single Add Tab */}
          <TabsContent value="single">
            <form onSubmit={handleSingleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">Prospect Name *</Label>
                <Input
                  id="name"
                  data-testid="prospect-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  data-testid="prospect-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="platform">Platform</Label>
                  <Select
                    value={formData.platform}
                    onValueChange={(value) => setFormData({ ...formData, platform: value })}
                  >
                    <SelectTrigger data-testid="platform-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {platforms.map(platform => (
                        <SelectItem key={platform} value={platform}>{platform}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="market">Market</Label>
                  <Select
                    value={formData.market}
                    onValueChange={(value) => setFormData({ ...formData, market: value })}
                  >
                    <SelectTrigger data-testid="market-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {markets.map(market => (
                        <SelectItem key={market} value={market}>{market}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message">Message/Note</Label>
                <Textarea
                  id="message"
                  data-testid="prospect-message-input"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={2}
                />
              </div>

              <div>
                <Label>Add to Lists</Label>
                <div className="space-y-2 mt-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="market-dev"
                      data-testid="market-dev-checkbox"
                      checked={formData.add_to_lists.includes('Market Development List')}
                      onCheckedChange={() => toggleList('Market Development List')}
                    />
                    <label htmlFor="market-dev" className="text-sm cursor-pointer">
                      Market Development List
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="top-10"
                      data-testid="top-10-checkbox"
                      checked={formData.add_to_lists.includes('Top 10 Contact List')}
                      onCheckedChange={() => toggleList('Top 10 Contact List')}
                    />
                    <label htmlFor="top-10" className="text-sm cursor-pointer">
                      Top 10 Contact List
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#E31837] hover:bg-[#C4122C]"
                  disabled={loading}
                  data-testid="save-prospect-button"
                >
                  {loading ? 'Saving...' : 'Save Prospect'}
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* Bulk Add Tab */}
          <TabsContent value="bulk">
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  Paste names from social media. Format: <strong>Name, optional notes</strong> (one per line)
                </p>
              </div>

              <div>
                <Label htmlFor="bulk-text">Prospects (one per line)</Label>
                <Textarea
                  id="bulk-text"
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  rows={6}
                  placeholder="John Smith, met at event
Jane Doe, interested in life insurance
Mike Johnson"
                  data-testid="bulk-text-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {bulkText.split('\n').filter(l => l.trim()).length} prospects to add
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Market</Label>
                  <Select value={bulkMarket} onValueChange={setBulkMarket}>
                    <SelectTrigger data-testid="bulk-market-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {markets.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Status</Label>
                  <Select value={bulkStatus} onValueChange={setBulkStatus}>
                    <SelectTrigger data-testid="bulk-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Platform</Label>
                  <Select value={bulkPlatform} onValueChange={setBulkPlatform}>
                    <SelectTrigger data-testid="bulk-platform-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {platforms.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                  data-testid="cancel-bulk-button"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkSubmit}
                  disabled={bulkLoading || !bulkText.trim()}
                  className="flex-1 bg-[#E31837] hover:bg-[#C4122C]"
                  data-testid="submit-bulk-button"
                >
                  {bulkLoading ? 'Adding...' : 'Add All Prospects'}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
